import _ from "lodash";
import { axiosInstance } from "./globals";
import * as userService from "./userService";
import * as util from "./util";
import { DataSourceRequest } from "@/model/DataSourceRequest";
import { DataSourceResult } from "@/model/DataSourceResult";
import { CustomerFilter } from "@/model/filters";
import { SmartVoiceToken } from '@/model/SmartVoiceToken';
import { CustomerFeatureInfo } from '@/model/CustomerFeatureInfo';
import { enumIntegrate } from '@/views/enums/enumIntegrate';

export async function settingsSubscription(data: {
  customerSubscriptionId: number,
  numberOfConcurrentMeetings: number,
  meetingConcurrentParticipantsLimit: number,
  maxParticipantPool: number,
}) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/CustomerSubscription/UpdateSubscriptionLimits", data, { headers })).data;
}

export async function purchaseAdditionalStorage(data: {
  customerSubscriptionId: number,
  storageSize: number,
}) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/CustomerSubscription/PurchaseAdditionalStorage", data, { headers })).data;
}

export async function purchaseAdditionalAiMinutes(data: {
  customerSubscriptionId: number,
  aiMinutes: number,
}) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/CustomerSubscription/PurchaseAdditionalAiMinutes", data, { headers })).data;
}
export async function getCustomers(filter: CustomerFilter, isExport: boolean = false, type?: string): Promise<any> {
  const headers = userService.getAuthHeaders();

  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
    filters: [],
    sorts: [
      { field: "id", dir: "desc" }
    ]
  };
  const filterCount = util.countPredicate([
    filter.q, filter.subscriptionPlanId, filter.areaId, filter.dateStart, filter.dateEnd,
    filter.customerType, filter.coType, filter.idType, filter.dateExpStart, filter.dateExpEnd, filter.customerSource,
    filter.featureType
  ]);
  if (filter.q && filterCount === 1) {
    if (!isExport)
      body.filters.push(
        { field: "isDeleted", operator: "eq", value: filter.isDeleted, logic: "and" }
      );
    body.filters[0].filters = [
      { field: "customerCode", operator: "contains", value: filter.q, logic: "or" },
      { field: "name", operator: "contains", value: filter.q, logic: "or" },
      { field: "email", operator: "contains", value: filter.q, logic: "or" },
      { field: "phoneNumber", operator: "contains", value: filter.q, logic: "or" }
    ];
  } else
    if (filter.isDeleted) {
      body.filters.push(
        { field: "isDeleted", operator: "eq", value: filter.isDeleted, logic: "and" }
      );
      if (filter.featureType === enumIntegrate.ALLOW_SUMMARY) {
        body.filters.push({
          field: "allowSummary", operator: "eq", value: 1, logic: "and"
        });
      }

      if (filter.featureType === enumIntegrate.ACTIVE_GM_TOKEN) {
        body.filters.push({
          field: "activeGMToken", operator: "eq", value: 1, logic: "and"
        });
      }

      if (filter.featureType === enumIntegrate.BOTH) {
        body.filters.push(
          { field: "allowSummary", operator: "eq", value: 1, logic: "and" },
          { field: "activeGMToken", operator: "eq", value: 1, logic: "and" }
        );
      }
    }
    else {
      if (filter.areaId) {
        body.filters.push(
          { field: "saleAreaId", operator: "eq", value: filter.areaId, logic: "and" }
        );
      }
      if (filter.subscriptionPlanId && filter.subscriptionPlanId.length != 0) {
        if (filter.subscriptionPlanId.length == 1)
          body.filters.push(
            { field: "subPlanId", operator: "eq", value: filter.subscriptionPlanId[0], logic: "and" }
          );
        else {
          const filters: any[] = [];
          filter.subscriptionPlanId.forEach((planId, i) => {
            filters.push({ field: "subPlanId", operator: "eq", value: planId, logic: i == filter.subscriptionPlanId.length - 1 ? "and" : "or" });
          });
          body.filters.unshift({ field: "all", operator: "eq", value: "", logic: "and" });
          body.filters[0].filters = filters;
        }
      }

      if (filter.dateStart) {
        body.filters.push(
          { field: "cusSubStartDate", operator: "gte", value: filter.dateStart, logic: "and" }
        );
      }

      if (filter.dateEnd) {
        body.filters.push(
          { field: "cusSubStartDate", operator: "lte", value: filter.dateEnd, logic: "and" }
        );
      }

      if (filter.dateExpStart) {
        body.filters.push(
          { field: "cusSubEndDate", operator: "gte", value: filter.dateExpStart, logic: "and" }
        );
      }

      if (filter.dateExpEnd) {
        body.filters.push(
          { field: "cusSubEndDate", operator: "lte", value: filter.dateExpEnd, logic: "and" }
        );
      }

      if (filter.customerType) {
        body.filters.push(
          { field: "customerType", operator: "eq", value: filter.customerType, logic: "and" }
        );
      }
      if (filter.customerSource) {

        body.filters.push(
          { field: "customerSource", operator: "contains", value: filter.customerSource, logic: "or" }
        );
      }

      if (filter.idType) {
        body.filters.push(
          { field: "idType", operator: "eq", value: filter.idType, logic: "and" }
        );
      }

      if (filter.coType) {
        body.filters.push(
          { field: "corporations", operator: "eq", value: filter.coType, logic: "and" }
        );
      }

      if (filter.createdTimeStart) {
        body.filters.push(
          { field: "createdAt", operator: "gte", value: filter.createdTimeStart, logic: "and" }
        );
      }

      if (filter.createdTimeEnd) {
        body.filters.push(
          { field: "createdAt", operator: "lte", value: filter.createdTimeEnd, logic: "and" }
        );
      }

      if (!isExport)
        body.filters.push(
          { field: "isDeleted", operator: "eq", value: filter.isDeleted, logic: "and" },
        );
      if (filter.featureType === enumIntegrate.ALLOW_SUMMARY) {
        body.filters.push({
          field: "allowSummary", operator: "eq", value: 1, logic: "and"
        });
      }

      if (filter.featureType === enumIntegrate.ACTIVE_GM_TOKEN) {
        body.filters.push({
          field: "activeGMToken", operator: "eq", value: 1, logic: "and"
        });
      }

      if (filter.featureType === enumIntegrate.BOTH) {
        body.filters.push(
          { field: "allowSummary", operator: "eq", value: 1, logic: "and" },
          { field: "activeGMToken", operator: "eq", value: 1, logic: "and" }
        );

        if (filter.q) {
          body.filters.unshift({ field: "all", operator: "eq", value: "", logic: "and" });
          body.filters[0].filters = [
            { field: "customerCode", operator: "contains", value: filter.q, logic: "or" },
            { field: "name", operator: "contains", value: filter.q, logic: "or" },
            { field: "email", operator: "contains", value: filter.q, logic: "or" },
            { field: "phoneNumber", operator: "contains", value: filter.q, logic: "or" }
          ];
        }
      }
    }

  if (!isExport) {
    return (await axiosInstance.post<DataSourceResult<any>>(
      `/Customer/GetCustomerList/${filter.subscriptionPlanStatus || "all"}`,
      body,
      { headers }
    )).data;
  } else {
    switch (type) {
      case "Users":
        return (await axiosInstance.post<DataSourceResult<any>>(
          `/Users/GetAllCustomer/ExportExcel`,
          body,
          { headers }
        )).data;

      default:
        return (await axiosInstance.post<DataSourceResult<any>>(
          `/Customer/GetCustomerList/${filter.subscriptionPlanStatus || "all"}/ExportExcel`,
          body,
          { headers }
        )).data;
    }
  }

}

export async function getDetails(id: number) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get(`/Customer/GetCustomerDetail/${id}`, { headers })).data;
}

export async function getFeatureConfigCustomer(id: number) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get(`/CustomerFeature/GetFeaturesByCustomerId/${id}`, { headers })).data;
}

export async function getCustomerFeature(id: number): Promise<CustomerFeatureInfo> {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get(`/CustomerFeature/GetFeaturesByCustomerId/${id}`, { headers })).data.data;
}

export async function newCustomer(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/Customer/AddNewCustomer", data, { headers })).data;
}


export async function updateCustomer(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/Customer/UpdateCustomer", data, { headers })).data;
}

export async function updateFeatureCustomer(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/CustomerFeature/AddFeaturesToCustomer", data, { headers })).data;
}


export async function deleteCustomer(id: number, typeDelete) {
  const headers = userService.getAuthHeaders();
  const params = { id, typeDelete };
  return (await axiosInstance.post("/Customer/DeleteCustomer", null, { headers, params })).data;
}



export async function getDocumentTypes(): Promise<any[]> {
  const headers = userService.getAuthHeaders();
  const params = { page: 1, pageSize: 100 };

  return (await axiosInstance.get("/DocumentType/GetDocumentTypeList", { headers, params })).data.data;
}

export async function getGMTokenByUser(id: number): Promise<any[]> {
  const headers = userService.getAuthHeaders();
  const params = {

  };

  return (await axiosInstance.get(`/UserToken/GetGMTokenByUser?userId=` + id, { headers, params })).data.data;
}


export async function newSubscription(customerId: number, subscriptionPlanId: number,
  numberOfConcurrentMeetings: number | null, meetingConcurrentParticipantsLimit: number | null, startTime: string, maxParticipantPool?: number | null) {
  const headers = userService.getAuthHeaders();
  const body = { customerId, subscriptionPlanId, startTime, numberOfConcurrentMeetings, meetingConcurrentParticipantsLimit, maxParticipantPool };

  return (await axiosInstance.post("/CustomerSubscription/AddNewCustomerSubscription", body, { headers })).data;
}


export async function renewSubscription(
  customerSubscriptionId: number,
  numberOfConcurrentMeetings: number | null,
  numOfMonth: number
) {
  const headers = userService.getAuthHeaders();
  const params = { NumofMonth: numOfMonth };
  const body = { customerSubscriptionId, numberOfConcurrentMeetings };

  return (await axiosInstance.post("/CustomerSubscription/RenewService", body, { headers, params })).data;
}


export async function upgradeSubscription(data) {
  const headers = userService.getAuthHeaders();
  const params = _.pick(data, ["dateStartToUpgrade", "upgradeOption", "numOfMonth"]);
  const body = _.pick(data, ["customerSubscriptionId", "newSubPlanId", "numberOfConcurrentMeetings", "meetingConcurrentParticipantsLimit", "maxParticipantPool"]);

  return (await axiosInstance.post("/CustomerSubscription/UpgradeService", body, { headers, params })).data;
}

export async function cancelAndRegisterNewSubscription(data) {
  const headers = userService.getAuthHeaders();
  const body = _.pick(data, ["customerId", "newSubscriptionPlanId", "numberOfConcurrentMeetings", "meetingConcurrentParticipantsLimit", "maxParticipantPool"]);

  return (await axiosInstance.post("/CustomerSubscription/CancelAndRegisterNewSubscription", body, { headers })).data;
}

export async function sendSubscriptionReminderEmail(customerSubscriptionId: number) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/CustomerSubscription/SendMailRenewService/" + customerSubscriptionId, { headers })).data;
}

export async function cancelService(customerSubscriptionId: number) {
  const headers = userService.getAuthHeaders();
  const params = { customerId: customerSubscriptionId };
  return (await axiosInstance.post("CustomerSubscription/CancelSubscription", null, { headers, params })).data;
}


export async function getListGMTokenByCustomer(customerId: string, filter: CustomerFilter): Promise<DataSourceResult<any>> {
  const headers = userService.getAuthHeaders();

  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
    filters: [],
    sorts: [
      { field: "id", dir: "desc" }
    ]
  };

  if (filter.q) {
    body.filters.push({
      field: "name", operator: "contains", value: filter.q, logic: "and"
    });
  }

  const res = (await axiosInstance.post<any>(`/UserToken/ListGMTokenByCustomer/?customerId=${customerId}`, body, { headers })).data;
  if (res.code === 0) {
    return res.data;
  }
  return null;
}

export async function createCustomerUserToken(userId: string) {
  const headers = userService.getAuthHeaders();
  const body = { userId: userId };
  return (await axiosInstance.post("UserToken/GenerateGMToken", body, { headers })).data;
}

export async function evictCustomerUserToken(userId: string) {
  const headers = userService.getAuthHeaders();
  const body = { userId: userId };
  return (await axiosInstance.post("UserToken/DeleteGMToken", body, { headers })).data;
}

export async function getSmartVoiceTokenByUser(id: number) {
  const headers = userService.getAuthHeaders();
  const params = {

  };

  return (await axiosInstance.get(`/CustomerSmartVoiceConfig/GetSmartVoiceConfigByCustomerId/` + id, { headers, params })).data.data;
}

export async function updateSmartVoiceToken(id: number, updatedData: SmartVoiceToken) {
  const headers = userService.getAuthHeaders();

  const body: any = {
    customerId: id,
    useOfSystem: updatedData.useOfSystem
  };

  if (updatedData.useOfSystem !== 1) {
    Object.assign(body, {
      authorization: updatedData.authorization,
      tokenId: updatedData.tokenId,
      tokenKey: updatedData.tokenKey,
      domainGRPC: updatedData.domainGRPC,
      portGRPC: updatedData.portGRPC
    });
  }

  const response = await axiosInstance.post("CustomerSmartVoiceConfig/save", body, { headers });
  return response.data;
}

export async function getSmartVoiceTokenSystem() {
  const headers = userService.getAuthHeaders();
  const params = {

  };
  return (await axiosInstance.get(`/Settings/GetByKey/SmartVoiceConfig`, { headers, params })).data.data;
}

export async function updateSmartVoiceTokenSystem(updatedData: SmartVoiceToken) {
  const headers = userService.getAuthHeaders();

  const body: any = {
    authorization: updatedData.authorization,
    tokenId: updatedData.tokenId,
    tokenKey: updatedData.tokenKey,
    domainGRPC: updatedData.domainGRPC,
    portGRPC: updatedData.portGRPC
  };

  const response = await axiosInstance.post("Settings/SaveSmartVoiceConfig", body, { headers });
  return response.data;
}

export async function withdrawCustomerConsent(customerId: number, reason: string) {
  const headers = userService.getAuthHeaders();
  const body = { customerId, reason };
  return (await axiosInstance.post("/UserConsent/WithdrawCustomerConsent", body, { headers })).data;
}
